/**
 * Arche Handoff payloads — CLIENT mirror of the canonical contract in
 * `packages/entities/src/ai-tools/handoff.ts` (the client package cannot
 * import workspace packages, so wire shapes are hand-mirrored here — same
 * convention as ./types.ts / ./agent-types.ts). The kind lists are pinned
 * together by `tests/architecture/arche-handoff-registry.test.ts`.
 *
 * A handoff payload is the ONLY sanctioned way one surface hands work to
 * another arche's UI. Producers: the assistant's `open_arche` prefill
 * (validated server-side against the canonical Zod schema before it ever
 * reaches the client) and the AI window's clickable ref-cards. Consumer:
 * the target arche's `useArcheHandoffIntake` hook — never ad-hoc reads of
 * navigation state (also pinned by the architecture test).
 *
 * `parseArcheHandoffPayload` re-validates on the client because navigation
 * state can be forged by any same-origin code path — treat it as untrusted
 * input, mirror the canonical bounds, reject unknown keys.
 */

export interface HandoffAddress {
  email: string;
  name?: string;
}

/** Open the Email composer pre-filled (or on an existing draft). Never sends. */
export interface EmailComposeHandoff {
  kind: 'email_compose';
  draftMessageId?: string;
  accountId?: string;
  to?: HandoffAddress[];
  cc?: HandoffAddress[];
  bcc?: HandoffAddress[];
  subject?: string;
  body?: string;
  replyToMessageId?: string;
  attachmentDocumentIds?: string[];
}

/** Open the Email arche at a message / thread / folder / named view. */
export interface EmailOpenHandoff {
  kind: 'email_open';
  messageId?: string;
  threadId?: string;
  folderId?: string;
  accountId?: string;
  view?: 'inbox' | 'drafts' | 'scheduled' | 'screener';
}

/**
 * GENERIC document handoff: open a Vault document (by reference) in ANY arche
 * with a registered document intake. The kind describes the PAYLOAD, never a
 * destination — the target arche comes from the open_arche call / openHandoff
 * caller. The receiving arche is the authoritative type gate (it refuses
 * unsupported files with a clear message) and opens the bytes as an UNSAVED
 * COPY — a later Save creates a new Vault document; the source is never
 * mutated.
 */
export interface OpenDocumentHandoff {
  kind: 'open_document';
  documentId: string;
}

export type ArcheHandoffPayload = EmailComposeHandoff | EmailOpenHandoff | OpenDocumentHandoff;
export type ArcheHandoffKind = ArcheHandoffPayload['kind'];

/** Must list exactly the canonical kinds (architecture-test pinned). */
export const ARCHE_HANDOFF_KINDS = ['email_compose', 'email_open', 'open_document'] as const;

/**
 * Arche-BOUND kinds → their single target (mirror of the canonical map).
 * Generic kinds (open_document) are absent — their target is validated
 * against the document-intake registry instead.
 */
export const HANDOFF_KIND_TARGET_ARCHE: Partial<Record<ArcheHandoffKind, string>> = {
  email_compose: 'arche_email',
  email_open: 'arche_email',
};

/** Document-intake registry (mirror): arches that can open a Vault document. */
export const ARCHE_DOCUMENT_INTAKE: Record<string, { label: string; accepts: string }> = {
  arche_image_editor: {
    label: 'Image Editor',
    accepts: 'raster images (PNG, JPEG, WebP, …), PSD, SVG',
  },
};

/** Would this payload be accepted by this arche? */
export function handoffTargetsArche(payload: ArcheHandoffPayload, archeId: string): boolean {
  const bound = HANDOFF_KIND_TARGET_ARCHE[payload.kind];
  if (bound) return bound === archeId;
  if (payload.kind === 'open_document') return archeId in ARCHE_DOCUMENT_INTAKE;
  return false;
}

// ---------------------------------------------------------------------------
// Validation (hand-rolled — no zod in the client). Mirrors the canonical
// schema: strict keys, bounded strings/arrays. Returns null on ANY violation
// — callers treat null as "ignore loudly" (toast + telemetry), never crash.
// ---------------------------------------------------------------------------

const MAX_ID = 64;
const MAX_EMAIL = 320;
const MAX_NAME = 200;
const MAX_SUBJECT = 500;
const MAX_BODY = 20_000;
const MAX_RECIPIENTS = 25;
const MAX_ATTACHMENTS = 10;
const EMAIL_OPEN_VIEWS = new Set(['inbox', 'drafts', 'scheduled', 'screener']);

/**
 * Non-empty bounded string — mirrors the canonical `z.string().min(1).max(n)`
 * used for ids, addresses, and contact names.
 */
function isBoundedString(v: unknown, max: number): v is string {
  return typeof v === 'string' && v.length >= 1 && v.length <= max;
}

/**
 * Bounded string that MAY be empty — mirrors the canonical
 * `z.string().max(n)` (no `.min(1)`) used for `subject` and `body`.
 *
 * These two fields are deliberately empty-able server-side: "compose to Bob,
 * I'll write it myself" is a valid prefill. Validating them with
 * `isBoundedString` rejected a payload the server had already accepted, so
 * the intake hook silently dropped a legal handoff. Keep the two helpers
 * distinct and match each field to the canonical schema's actual bound —
 * boundary parity is covered by __tests__/handoff.test.ts.
 */
function isBoundedStringAllowingEmpty(v: unknown, max: number): v is string {
  return typeof v === 'string' && v.length <= max;
}

function isOptionalId(v: unknown): boolean {
  return v === undefined || isBoundedString(v, MAX_ID);
}

function isAddressList(v: unknown, max: number): boolean {
  if (v === undefined) return true;
  if (!Array.isArray(v) || v.length > max) return false;
  return v.every((a) => {
    if (typeof a !== 'object' || a === null) return false;
    const keys = Object.keys(a as Record<string, unknown>);
    if (!keys.every((k) => k === 'email' || k === 'name')) return false;
    const { email, name } = a as { email?: unknown; name?: unknown };
    if (!isBoundedString(email, MAX_EMAIL) || !email.includes('@')) return false;
    return name === undefined || isBoundedString(name, MAX_NAME);
  });
}

function hasOnlyKeys(obj: Record<string, unknown>, allowed: readonly string[]): boolean {
  return Object.keys(obj).every((k) => allowed.includes(k));
}

/** Validate an untrusted value into a typed handoff payload, or null. */
export function parseArcheHandoffPayload(value: unknown): ArcheHandoffPayload | null {
  if (typeof value !== 'object' || value === null) return null;
  const obj = value as Record<string, unknown>;

  if (obj.kind === 'email_compose') {
    if (
      !hasOnlyKeys(obj, [
        'kind',
        'draftMessageId',
        'accountId',
        'to',
        'cc',
        'bcc',
        'subject',
        'body',
        'replyToMessageId',
        'attachmentDocumentIds',
      ])
    ) {
      return null;
    }
    if (!isOptionalId(obj.draftMessageId) || !isOptionalId(obj.accountId)) return null;
    if (!isOptionalId(obj.replyToMessageId)) return null;
    if (
      !isAddressList(obj.to, MAX_RECIPIENTS) ||
      !isAddressList(obj.cc, MAX_RECIPIENTS) ||
      !isAddressList(obj.bcc, MAX_RECIPIENTS)
    ) {
      return null;
    }
    // subject/body mirror `z.string().max(n)` — empty is legal (see helper).
    if (obj.subject !== undefined && !isBoundedStringAllowingEmpty(obj.subject, MAX_SUBJECT)) {
      return null;
    }
    if (obj.body !== undefined && !isBoundedStringAllowingEmpty(obj.body, MAX_BODY)) return null;
    if (obj.attachmentDocumentIds !== undefined) {
      const ids = obj.attachmentDocumentIds;
      if (
        !Array.isArray(ids) ||
        ids.length > MAX_ATTACHMENTS ||
        !ids.every((id) => isBoundedString(id, MAX_ID))
      ) {
        return null;
      }
    }
    return obj as unknown as EmailComposeHandoff;
  }

  if (obj.kind === 'email_open') {
    if (!hasOnlyKeys(obj, ['kind', 'messageId', 'threadId', 'folderId', 'accountId', 'view'])) {
      return null;
    }
    if (
      !isOptionalId(obj.messageId) ||
      !isOptionalId(obj.threadId) ||
      !isOptionalId(obj.folderId) ||
      !isOptionalId(obj.accountId)
    ) {
      return null;
    }
    if (obj.view !== undefined && !EMAIL_OPEN_VIEWS.has(obj.view as string)) return null;
    return obj as unknown as EmailOpenHandoff;
  }

  if (obj.kind === 'open_document') {
    if (!hasOnlyKeys(obj, ['kind', 'documentId'])) return null;
    if (!isBoundedString(obj.documentId, MAX_ID)) return null;
    return obj as unknown as OpenDocumentHandoff;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Ref-card registry: which tool-result ref types are openable, and the
// handoff payload a click produces. Adding an entry here is how an arche's
// refs become clickable cards in the AI window.
// ---------------------------------------------------------------------------

export interface RefLike {
  id: string;
  type: string;
  label: string;
}

/** Build the click-through handoff for a tool-result ref, or null (not openable). */
export function refOpenHandoff(ref: RefLike): ArcheHandoffPayload | null {
  if (!isBoundedString(ref.id, MAX_ID)) return null;
  switch (ref.type) {
    case 'email_message':
      return { kind: 'email_open', messageId: ref.id };
    case 'email_thread':
      return { kind: 'email_open', threadId: ref.id };
    case 'email_draft':
      return { kind: 'email_compose', draftMessageId: ref.id };
    case 'email_scheduled_send':
      return { kind: 'email_open', view: 'scheduled' };
    default:
      return null;
  }
}
